import {
  UPLOAD_HEIGHT_HEADER, UPLOAD_PAGE_HEADER, UPLOAD_WIDTH_HEADER,
  type CloudBookmark, type CloudCatalogEntry, type CloudProgress, type CloudSyncState,
  type UploadKind, type UploadStatus,
} from '@tanko/shared'
import type { PublicationSnapshot } from './upload-session'

export interface CloudCategory { id: string; name: string; private: boolean; count: number }
export interface CloudComic {
  id: string; title: string; author: string; pageCount: number; coverPage: number
  syncState: CloudSyncState; createdAt: number; updatedAt: number
}
export interface CloudComicDetail extends CloudComic { categoryIds: string[] }
/** 清單項目附帶目錄項上的閱讀進度摘要；沒有進度為 null。詳情端點不帶這個欄位。 */
export interface CloudProgressSummary { page: number; updatedAt: number }
export interface CloudListedComic extends CloudComic { progress: CloudProgressSummary | null }
export interface CloudComicBatch { items: CloudListedComic[]; nextCursor: string | null }
/** 集合：作品之上的分組。成員只列目錄裡的作品，各帶頁數與進度摘要供集合格算已讀本數。 */
export interface CollectionMember {
  comicId: string; position: number; title: string; pageCount: number; coverPage: number; syncState: CloudSyncState
  progress: CloudProgressSummary | null
}
export interface CollectionSummary {
  id: string; title: string; mode: CollectionMode; coverComicId: string | null; categoryIds: string[]
  createdAt: number; updatedAt: number; memberCount: number; members: CollectionMember[]
}
/** 集合頁用：成員的完整作品欄位，依 position 排序。 */
export interface CollectionDetail extends CollectionSummary { comics: CloudListedComic[] }
/** 開合併冊時一次載入全部成員的閱讀狀態。 */
export interface CollectionMemberState { comicId: string; progress: CloudProgress | null; bookmarks: CloudBookmark[]; coverPage: number }
export interface CollectionState { members: CollectionMemberState[]; serverTime: number }
export type CollectionMode = 'series' | 'merged'
export interface CloudReadingStateResponse {
  progress: CloudProgress | null
  bookmarks: CloudBookmark[]
  coverPage: number
  serverTime: number
}
export interface CloudMutationResponse<T> { accepted: boolean; current: T; serverTime: number }
export interface UploadReceipt {
  page: number
  kind: UploadKind
  attemptId: string
  etag: string
  size: number
  width: number
  height: number
}
export interface CloudPageManifest { page: number; w: number; h: number }
export interface CloudPageManifestBatch { items: CloudPageManifest[]; nextCursor: string | null }
export interface UploadSessionState {
  id: string
  pageCount: number
  status: UploadStatus
  verificationCursor: number
  revision: number
  comicId: string | null
  lastErrorPage: number | null
  createdAt: number
  updatedAt: number
  expiresAt: number
  received: UploadReceipt[]
}

export class CloudApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string) { super(message) }
}
export class AuthenticationRequiredError extends CloudApiError {
  constructor() { super(401, 'AUTHENTICATION_REQUIRED', '雲端登入已失效，請重新登入') }
}

export class CloudApiClient {
  constructor(private readonly fetcher?: typeof fetch) {}

  /** 目錄註冊：整批原子，重送同一批不建立第二筆。一次至多 CATALOG_REGISTER_BATCH_SIZE 本。 */
  registerCatalog(comics: readonly CloudCatalogEntry[]) {
    return this.json<{ registered: string[] }>('/catalog/register', { method: 'POST', body: JSON.stringify({ comics }) })
  }
  createUpload(uploadId: string, pageCount: number) {
    return this.json<UploadSessionState>('/uploads', { method: 'POST', body: JSON.stringify({ uploadId, pageCount }) })
  }
  getUpload(uploadId: string) { return this.json<UploadSessionState>(`/uploads/${uploadId}`) }
  verifyUpload(uploadId: string) { return this.json<UploadSessionState>(`/uploads/${uploadId}/verify`, { method: 'POST' }) }
  publishUpload(uploadId: string, publication: PublicationSnapshot) {
    // 以具名欄位組裝而不直接序列化：IndexedDB 裡舊版凍結的 snapshot 可能殘留 categoryIds，
    // 而 API 對未知欄位一律 422。
    const { comicId, title, author, coverPage, progress, bookmarks } = publication
    const body = JSON.stringify({ comicId, title, author, coverPage, progress, bookmarks })
    return this.json<{ comicId: string }>(`/uploads/${uploadId}/publish`, { method: 'POST', body })
  }
  cancelUpload(uploadId: string) { return this.empty(`/uploads/${uploadId}`, { method: 'DELETE' }) }
  comics(options: { q?: string; categoryIds?: string[]; cursor?: string } = {}) {
    const query = new URLSearchParams({ limit: '100' })
    if (options.q) query.set('q', options.q)
    if (options.categoryIds?.length) query.set('categoryIds', options.categoryIds.join(','))
    if (options.cursor) query.set('cursor', options.cursor)
    return this.json<CloudComicBatch>(`/comics?${query}`)
  }
  comic(comicId: string) { return this.json<CloudComicDetail>(`/comics/${comicId}`) }
  updateComic(comicId: string, patch: { title?: string; author?: string; coverPage?: number; categoryIds?: string[] }) {
    return this.json<CloudComicDetail>(`/comics/${comicId}`, { method: 'PATCH', body: JSON.stringify(patch) })
  }
  deleteComic(comicId: string) { return this.empty(`/comics/${comicId}`, { method: 'DELETE' }) }
  /*
    批次動作：一次請求處理多本，原子套用。分類指派在伺服器端以集合運算完成，因此
    呼叫端不必先讀出每一本目前的指派——那個「先讀再寫」除了每本兩次往返，還會在
    兩次之間把別處對同一本的變動蓋掉。
  */
  bulkAssignCategories(comicIds: readonly string[], categoryIds: readonly string[], mode: 'add' | 'remove') {
    return this.empty('/comics/bulk/categories', {
      method: 'POST', body: JSON.stringify({ comicIds: [...comicIds], categoryIds: [...categoryIds], mode }),
    })
  }
  bulkEdit(field: 'title' | 'author', items: readonly { id: string; value: string }[]) {
    return this.empty('/comics/bulk/edit', { method: 'POST', body: JSON.stringify({ field, items: [...items] }) })
  }
  bulkDeleteComics(comicIds: readonly string[]) {
    return this.empty('/comics/bulk/delete', { method: 'POST', body: JSON.stringify({ comicIds: [...comicIds] }) })
  }
  categories() { return this.json<{ items: CloudCategory[] }>('/categories') }
  createCategory(name: string, isPrivate = false) {
    return this.json<CloudCategory>('/categories', { method: 'POST', body: JSON.stringify({ name, private: isPrivate }) })
  }
  /** 未指定隱私時不送那個欄位，伺服器因此維持原值——更名不該順手改掉它。 */
  renameCategory(categoryId: string, name: string, isPrivate?: boolean) {
    return this.json<{ id: string; name: string; private: boolean }>(`/categories/${categoryId}`, {
      method: 'PATCH', body: JSON.stringify(isPrivate === undefined ? { name } : { name, private: isPrivate }),
    })
  }
  /** 每個隱私分類底下有哪些作品。一次查詢，不逐本問。 */
  privateCategoryMembers() {
    return this.json<{ items: { categoryId: string; comicIds: string[] }[] }>('/categories/private-members')
  }
  deleteCategory(categoryId: string) { return this.empty(`/categories/${categoryId}`, { method: 'DELETE' }) }
  collections() { return this.json<{ items: CollectionSummary[] }>('/collections') }
  collection(collectionId: string) { return this.json<CollectionDetail>(`/collections/${collectionId}`) }
  collectionState(collectionId: string) { return this.json<CollectionState>(`/collections/${collectionId}/state`) }
  createCollection(title: string, comicIds: readonly string[], mode: CollectionMode = 'series') {
    return this.json<CollectionSummary>('/collections', { method: 'POST', body: JSON.stringify({ title, comicIds: [...comicIds], mode }) })
  }
  updateCollection(collectionId: string, patch: { title?: string; coverComicId?: string | null; categoryIds?: string[]; mode?: CollectionMode }) {
    return this.json<CollectionSummary>(`/collections/${collectionId}`, { method: 'PATCH', body: JSON.stringify(patch) })
  }
  addCollectionMembers(collectionId: string, comicIds: readonly string[]) {
    return this.json<CollectionSummary>(`/collections/${collectionId}/members`, { method: 'POST', body: JSON.stringify({ comicIds: [...comicIds] }) })
  }
  removeCollectionMember(collectionId: string, comicId: string) {
    return this.json<{ dissolved: boolean }>(`/collections/${collectionId}/members/${comicId}`, { method: 'DELETE' })
  }
  reorderCollection(collectionId: string, comicIds: readonly string[]) {
    return this.json<CollectionSummary>(`/collections/${collectionId}/order`, { method: 'PUT', body: JSON.stringify({ comicIds: [...comicIds] }) })
  }
  dissolveCollection(collectionId: string) { return this.empty(`/collections/${collectionId}`, { method: 'DELETE' }) }
  deleteCollectionWithMembers(collectionId: string) {
    return this.empty(`/collections/${collectionId}/delete-with-members`, { method: 'POST', body: JSON.stringify({}) })
  }
  readingState(comicId: string) { return this.json<CloudReadingStateResponse>(`/comics/${comicId}/state`) }
  saveProgress(comicId: string, progress: CloudProgress) {
    return this.json<CloudMutationResponse<CloudProgress>>(`/comics/${comicId}/progress`, { method: 'PUT', body: JSON.stringify(progress) })
  }
  saveBookmark(comicId: string, bookmark: Omit<CloudBookmark, 'deleted'>) {
    const { id, ...body } = bookmark
    return this.json<CloudMutationResponse<CloudBookmark>>(`/comics/${comicId}/bookmarks/${id}`, { method: 'PUT', body: JSON.stringify(body) })
  }
  deleteBookmark(comicId: string, bookmarkId: string, mutation: Pick<CloudBookmark, 'updatedAt' | 'mutationId'>) {
    return this.json<CloudMutationResponse<CloudBookmark>>(`/comics/${comicId}/bookmarks/${bookmarkId}`, { method: 'DELETE', body: JSON.stringify(mutation) })
  }
  uploadPart(uploadId: string, page: number, kind: UploadKind, attemptId: string, blob: Blob, width: number, height: number, signal: AbortSignal) {
    return this.json<UploadReceipt>(`/uploads/${uploadId}/pages/${page}/${kind}/${attemptId}`, {
      method: 'PUT', body: blob, signal,
      headers: { 'Content-Type': 'image/webp', [UPLOAD_WIDTH_HEADER]: String(width), [UPLOAD_HEIGHT_HEADER]: String(height) },
    })
  }
  comicPages(comicId: string, cursor?: string) {
    const query = new URLSearchParams({ limit: '512' })
    if (cursor !== undefined) query.set('cursor', cursor)
    return this.json<CloudPageManifestBatch>(`/comics/${comicId}/pages?${query}`)
  }
  /** 全尺寸頁面圖。 */
  pageImage(comicId: string, page: number, signal: AbortSignal): Promise<Blob> {
    return this.webp(`/comics/${comicId}/pages/${page}/image`, signal)
  }
  /**
   * 封面縮圖，一本一張。pending 作品沒有，會回 404。
   *
   * `version` 是作品的 updatedAt：換封面時它一定會變（兩者在同一個 UPDATE 裡），因此
   * 網址一變就會重新取得。伺服器據此把封面設為可長期快取，書架重訪不必每張都走網路。
   */
  coverImage(comicId: string, version: number, signal: AbortSignal): Promise<Blob> {
    return this.webp(`/comics/${comicId}/cover?v=${version}`, signal)
  }
  /** 換封面：縮圖本身當 body，來源頁碼與尺寸走 header；回傳更新後的 detail。 */
  replaceCover(comicId: string, page: number, blob: Blob, width: number, height: number, signal?: AbortSignal) {
    return this.json<CloudComicDetail>(`/comics/${comicId}/cover`, {
      method: 'PUT', body: blob, signal,
      headers: {
        'Content-Type': 'image/webp', [UPLOAD_PAGE_HEADER]: String(page),
        [UPLOAD_WIDTH_HEADER]: String(width), [UPLOAD_HEIGHT_HEADER]: String(height),
      },
    })
  }
  private async webp(path: string, signal: AbortSignal): Promise<Blob> {
    const response = await this.request(path, { signal, headers: { Accept: 'image/webp' } })
    const type = response.headers.get('Content-Type')?.split(';')[0].trim().toLowerCase()
    if (type === 'text/html') throw new AuthenticationRequiredError()
    if (type !== 'image/webp') throw new CloudApiError(503, 'INVALID_IMAGE_RESPONSE', '私人雲端服務沒有回傳 WebP 圖片')
    const blob = await response.blob()
    if (!blob.size) throw new CloudApiError(503, 'INVALID_IMAGE_RESPONSE', '私人雲端服務回傳了空白圖片')
    return blob
  }

  private async empty(path: string, init: RequestInit) { await this.request(path, init) }
  private async json<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await this.request(path, init)
    const contentType = response.headers.get('Content-Type')?.toLowerCase() ?? ''
    if (contentType.includes('text/html')) throw new AuthenticationRequiredError()
    if (!contentType.includes('application/json')) {
      throw new CloudApiError(503, 'INVALID_RESPONSE', '私人雲端服務回傳了無法辨識的內容')
    }
    return response.json() as Promise<T>
  }
  private async request(path: string, init: RequestInit) {
    const method = init.method ?? 'GET'
    const headers = new Headers(init.headers)
    if (!headers.has('Accept')) headers.set('Accept', 'application/json')
    if (init.body && !(init.body instanceof Blob)) headers.set('Content-Type', 'application/json')
    if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) headers.set('X-Tanko-Request', '1')
    const fetcher = this.fetcher ?? globalThis.fetch
    const response = await fetcher(`/api${path}`, { ...init, method, headers, credentials: 'same-origin', redirect: 'manual' })
    if (response.type === 'opaqueredirect' || (response.status >= 300 && response.status < 400) || response.status === 401 || response.status === 403) {
      throw new AuthenticationRequiredError()
    }
    if (!response.ok) {
      let details: { error?: { code?: string; message?: string; requestId?: string } } = {}
      try { details = await response.json() as typeof details } catch { /* 以通用訊息取代非 JSON 錯誤。 */ }
      const message = details.error?.message ?? '雲端操作未完成，請稍後重試'
      // 伺服器端的錯誤訊息刻意不帶細節，因此 5xx 要把 requestId 帶上：畫面上的那句話
      // 才對得回伺服器的記錄，否則只知道「壞了」而不知道壞在哪。
      const correlated = response.status >= 500 && details.error?.requestId
        ? `${message}（requestId: ${details.error.requestId}）`
        : message
      throw new CloudApiError(response.status, details.error?.code ?? 'REQUEST_FAILED', correlated)
    }
    return response
  }
}
